import { useContext, useEffect, useRef, useState } from "react";
import PageContainer, { PageContainerBase } from "../../Common/Template/PageContainer";
import { Lead, Partner, PartnerBusinessContract } from "../../../Model/PartnerModels";
import { SystemActionCategory, SystemDataLoadingStatus, SystemOperation, SystemPageType } from "../../../Model/SystemModels";
import { useNavigate } from "react-router-dom";
import { HttpHelper } from "../../../Core/Http/HttpHelper";
import { SystemCore } from "../../../Core/System/SystemCore";
import { Loader } from "../../Common/Loader/Loader";
import { ContainerType, PageDefinitionContainer } from "../../Common/Template/PageDefinitionContainer";
import { ActionArea } from "../../Common/Template/Action/ActionArea";
import { ActionEntry } from "../../Common/Template/Action/ActionEntry";
import { ListHeaderEntry } from "../../Common/Template/List/ListHeaderEntry";
import { PartnerLeadCard } from "../PartnerLeadCard/PartnerLeadCard";
import { t } from "i18next";
import { IPartnerInformationContext, PartnerInformationContext } from "../../../ApplicationCode/Partner/PartnerInformationContext";
import { CommonLeadModal } from "../../Common/CommonLeadModal/CommonLeadModal";
import { AdminService } from "../../../ApplicationCode/Admin/AdminService";
import { LeadConfigChecklistDetails, LeadToLiveChecklist, SaveLeadConfigChecklist } from "../../Common/LeadDashboard/LeadDashboard";
import { IndustyType } from "../../../Model/DemoModels";
import { MessageType, ToastService } from "../../../Core/Toast/ToastService";
import { PartnerService } from "../../../ApplicationCode/Partner/PartnerService";
import { DataSecurityStatus } from "../../../Model/AdminModel";

export const PartnerLeadList : React.FC = () => {
    const adminService = new AdminService();
    const partnerService = new PartnerService();
    
    const currentPage = useRef<PageContainerBase>(null);
    const cardRef = useRef<PageContainerBase>(null);
    
    const partnerStateContext: IPartnerInformationContext = useContext(PartnerInformationContext);

    const navigate = useNavigate();
    
    const [dataStatus, setDataStatus] = useState<SystemDataLoadingStatus>(SystemDataLoadingStatus.ToLoad);
    const [message, setMessage] = useState<string>(t('partnerCenter:partnerLeadList:loading')!);
    const [endpoint, setEndpoint] = useState<string>("");
    const [listTitle, setListTitle] = useState<string>("");
    const [partner, setPartner] = useState<Partner>(new Partner());
    const [showLeadModal, setShowLeadModal] = useState<boolean>(false);
    const [selectedLead, setSelectedLead] = useState<Lead | undefined>(undefined);
    const [leadConfigDetails, setLeadConfigDetails] = useState<LeadConfigChecklistDetails>(new LeadConfigChecklistDetails());
    const [industryTypes, setIndustryTypes] = useState<IndustyType[]>([]);
    const [leadDataSaveInProgress, setLeadDataSaveInProgress] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<Object[]>([]);
    const [partnerBusinessContract, setPartnerBusinessContract] = useState(new PartnerBusinessContract());
    const [dsStates, setDsStates] = useState<DataSecurityStatus[]>([]);
    
    const formatDate = (date: Date) => {
        const userLang = navigator.language || navigator.languages[0] || "en-US";
        return new Intl.DateTimeFormat(userLang, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }).format(date);
      };

    const buildListEndpoint = () =>{
        setDataStatus(SystemDataLoadingStatus.Loading);

        const workflowCode = HttpHelper.getParameter('code');
        const workflowCodeName = HttpHelper.getParameter('codeName');
        const leadFilter = HttpHelper.getParameter('leadFilter');
        const activeLeads = HttpHelper.getParameter('activeLeads');
        let buildedEndpoint = "/odata/lead";
        let currentTitle = t('partnerCenter:partnerLeadList:leadList');
        
        if (partnerStateContext.partner.id === 1) {
            if (workflowCode != null && workflowCode !== "") {
                currentTitle += " ("+workflowCodeName+")";
                buildedEndpoint += '?$filter=(email gt \'\') and businessLineCode eq \'SO-SMART\' and statusCode eq \''+workflowCode+'\'&$orderby=createdOn desc';
            }
            else if (activeLeads != null && activeLeads !== "") {
                currentTitle += t('partnerCenter:partnerLeadList:active');
                buildedEndpoint += '?$filter=(email gt \'\') and businessLineCode eq \'SO-SMART\' and statusCode le \'85\'&$orderby=createdOn desc';
            }
            else if ((leadFilter !== null) && (leadFilter !== "")) {
                switch(leadFilter) {
                    case "leadWin":
                        currentTitle += " (Win)";
                        buildedEndpoint += '?$filter=(email gt \'\') and statusCode eq \'95\' or finalOutcome eq \'Win\'&$orderby=systemCreationDate desc';
                        break;
                    case "leadLost":
                        currentTitle += " (Lost)";
                        buildedEndpoint += '?$filter=(email gt \'\') and statusCode eq \'96\' or finalOutcome eq \'Lost\'&$orderby=systemCreationDate desc';
                        break;
                    case "leadUnqualified":
                        currentTitle += " (Unqualified)";
                        buildedEndpoint += '?$filter=(email gt \'\') and statusCode eq \'97\' or finalOutcome eq \'Unqualified\'&$orderby=systemCreationDate desc';
                        break;
                }
            }
            else {
                currentTitle += t('partnerCenter:partnerLeadList:total');
                buildedEndpoint += '?filter=(email gt \'\')&$orderby=systemCreationDate desc';
            }
        }
        else {
            if (workflowCode != null && workflowCode !== "") {
                currentTitle += " ("+workflowCodeName+")";
                buildedEndpoint += '?$filter=(email gt \'\') and statusCode eq \''+workflowCode+'\' and partnerId eq '+partnerStateContext.partner.id+'&$orderby=createdOn desc';
            }
            else if (activeLeads != null && activeLeads !== "") {
                currentTitle += t('partnerCenter:partnerLeadList:active');
                buildedEndpoint += '?$filter=(email gt \'\') and partnerId eq '+partnerStateContext.partner.id+' and statusCode le \'85\'&$orderby=createdOn desc';
            }
            else {
                currentTitle += t('partnerCenter:partnerLeadList:total');
                buildedEndpoint += '?$filter=(email gt \'\') and partnerId eq '+partnerStateContext.partner.id+'&$orderby=systemCreationDate desc';
            }
        }
        
        setPartner(partnerStateContext.partner);
        setEndpoint(buildedEndpoint);
        setListTitle(currentTitle);
        setDataStatus(SystemDataLoadingStatus.Loaded);
    }
    
    useEffect(() => {
        if (dataStatus === SystemDataLoadingStatus.ToLoad) {
            partnerService.getPartnerBusinessContract(partnerStateContext.partner.partnerBusinessContractCode)
            .then((resp) => {
                setPartnerBusinessContract(resp);
                adminService.getDsStatusValue('Lead')
                .then((resp) => {
                    setDsStates(resp);
                    buildListEndpoint();
                })
            });
        }
    }, [dataStatus]);
    
    const setNewRecord = () => {
        let newRecord = new Lead();
        newRecord.partnerId = partner.id;
        newRecord.legalEntityCode = partner.legalEntityCode;
        newRecord.distributorCode = partner.distributorCode;
        newRecord.partnerBusinessContractCode = partner.partnerBusinessContractCode;
        newRecord.businessLineCode = partner.businessLineCode;
        newRecord.languageCode = partner.languageCode;
        newRecord.marketingQualificationCarriedOut = true;
        if (partner.legalEntityCode === "ESS")
            newRecord.country = "US";
        cardRef.current?.setRecord(newRecord);
        cardRef.current?.openPage(SystemOperation.Insert);
    }
    
    const buildShopUrl = (): string => {
        if (partnerStateContext.partner.legalEntityCode === 'ESS')
            return process.env.REACT_APP_SHOP_URL+".us/";
        else
            return process.env.REACT_APP_SHOP_URL+".it/";
    }

    const getLeadData = (lead: Lead): Promise<boolean> => {
        return new Promise((resolve, reject) =>{            
            adminService.getLeadConfigDetails(lead).then((details) => {
                setLeadConfigDetails(details);
        
                adminService.getIndustryTypes().then((respIndustries) => {
                    setIndustryTypes(respIndustries);
                    resolve(true);
                })
                .catch(err => {
                    ToastService.showMessage(MessageType.Error, err);
                    reject(false);
                })
            })
            .catch((err) => {
                ToastService.showMessage(MessageType.Error, err);
                reject(false);
            });
        })
    }
    
    const checkLeadModalData = (configuration: SaveLeadConfigChecklist): Object[] => {
        let validationErrors : object[] = [];

        if (! configuration.lead.firstname)
            validationErrors.push({
                reference: "firstname",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00001Text')!,
                applyToField: true
            });
        if (! configuration.lead.lastname)
            validationErrors.push({
                reference: "lastname",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00002Text')!,
                applyToField: true
            });
        if (! configuration.lead.companyName)
            validationErrors.push({
                reference: "companyName",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00003Text')!,
                applyToField: true
            });

        if (! configuration.lead.address)
            validationErrors.push({
                reference: "address",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00004Text')!,
                applyToField: true
            });
        if (! configuration.lead.zipCode)
            validationErrors.push({
                reference: "zipCode",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00004Text')!,
                applyToField: true
            });
        if (! configuration.lead.city)
            validationErrors.push({
                reference: "city",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00004Text')!,
                applyToField: true
            });
        if (! configuration.lead.county)
            validationErrors.push({
                reference: "county",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00004Text')!,
                applyToField: true
            });
        if (! configuration.lead.country)
            validationErrors.push({
                reference: "country",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00004Text')!,
                applyToField: true
            });

        if (! configuration.lead.email)
            validationErrors.push({
                reference: "email",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00004Text')!,
                applyToField: true
            });
        
        if (configuration.lead.full_Users_No === 0)
            validationErrors.push({
                reference: "fullUsersNo",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00004Text')!,
                applyToField: true
            });

        if (configuration.lead.evaluationMode === 0)
            validationErrors.push({
                reference: "evaluationMode",
                error: t('demoCenter:TrialModal:Dialog:FieldError:Err00007Text')!,
                applyToField: true,
            });

        setValidationErrors(validationErrors);
        return validationErrors;
    }

    const onSaveLeadModal = (configuration: SaveLeadConfigChecklist) => {
        if (checkLeadModalData(configuration).length > 0)
            return false;
   
        setLeadDataSaveInProgress(true);
        let client = HttpHelper.buildAxiosInstance();

        client.post('/api/system/lead/checklist/config/save', configuration).then(() => {
            setShowLeadModal(false);
            setLeadDataSaveInProgress(false);
            currentPage.current?.reloadData();
        })
        .catch(() => {
            ToastService.showMessage(MessageType.Error, t('leadDashboard:errors:text00002Err')!);
            setLeadDataSaveInProgress(false);
        });

        return true;
    }

    const weekday = [
        t('partnerCenter:partnerLeadList:weekday:sunday')!,
        t('partnerCenter:partnerLeadList:weekday:monday')!,
        t('partnerCenter:partnerLeadList:weekday:tuesday')!,
        t('partnerCenter:partnerLeadList:weekday:wednesday')!,
        t('partnerCenter:partnerLeadList:weekday:thursday')!,
        t('partnerCenter:partnerLeadList:weekday:friday')!,
        t('partnerCenter:partnerLeadList:weekday:saturday')!,
    ];

    const setAsCustomer = (leadId: number) => {        
        let instance = HttpHelper.buildAxiosInstance();
        let endpoint = "/api/setup/partner/lead/"+leadId+"/setascustomer";
        var promise = new Promise((resolve, reject) => {
            instance.post(endpoint)
            .then(() => {
                currentPage.current?.reloadData();
                resolve(true);
            })
            .catch(() => reject(false))
        })

        ToastService.showPromiseMessage(promise, t('partnerCenter:partnerLeadList:actions:setAsCustomerInProgress')!, t('partnerCenter:partnerLeadList:actions:setAsCustomerSuccess')!, t('partnerCenter:partnerLeadList:actions:setAsCustomerError')!);
    }

    const sendTrialExpirationEmail = (leadId: number, partnerId: number) => {
        let instance = HttpHelper.buildAxiosInstance();
        let endpoint = "/api/crm/partner/"+partnerId+"/lead/"+leadId+"/sendtrialexpirationemail"
        let promise = new Promise((resolve, reject) => {
            instance.post(endpoint)
            .then(() => { resolve(true) })
            .catch((err) => {
                ToastService.showMessage(MessageType.Error, err.response.data.message);
                reject(false);
            })
        });

        ToastService.showPromiseMessage(promise, t('partnerCenter:partnerLeadList:actions:sendTrialExpirationEmailInProgress')!, t('partnerCenter:partnerLeadList:actions:sendTrialExpirationEmailSuccess')!, "");
    }
    
    if (!SystemCore.isFinishedDataStatus(dataStatus))
        return <Loader text={message}></Loader>;
    else
    return (
        <>
            <PageContainer onNewRecord={setNewRecord} endpoint={endpoint} pageType={SystemPageType.List} title={listTitle} ref={currentPage} cardRef={cardRef} backAllowed insertAllowed updateAllowed>
                <PageDefinitionContainer type={ContainerType.Action}>
                    <ActionArea category={SystemActionCategory.Navigation}>
                        <ActionEntry name="LeadQualification" label={t("adminCenter:leadList:actions:leadQualification")} iconName="PartyLeader" isPromoted runOnRec onClick={(req:any)=>{
                                setSelectedLead(req);
                                getLeadData(req).then(()=>{setShowLeadModal(true)});
                            }}>
                        </ActionEntry>
                        {partnerBusinessContract.cspLicenseManagementEnable ? (
                            <ActionEntry name="OpenShopAddress" label={t('partnerCenter:partnerLeadList:actions:configureOffer')!} iconName="ConfigurationSolid" runOnRec isPromoted onClick={(req:any)=>{
                                window.open(buildShopUrl());
                            }}></ActionEntry>
                        ): (
                            <ActionEntry name="ManageOffer" label={t('partnerCenter:partnerLeadList:actions:manageOffer')!} iconName="AccountActivity" runOnRec isPromoted onClick={(req:any)=>{
                                navigate("/app/partner/leadOfferList?leadId="+req.id);
                            }}></ActionEntry>
                        )}
                        <ActionEntry name="OpenBookingLeadAvailability" label={t('partnerCenter:partnerLeadList:actions:bookCustomerDemo')!} iconName="JoinOnlineMeeting" runOnRec onClick={(req:any)=>{
                                navigate("/app/partner/bookingAvailability?source=Partner&mode=lead&leadId="+req.id+"&leadName="+req.companyName);
                            }}>
                        </ActionEntry>
                        <ActionEntry name="OpenBookingInternalAvailability" label={t('partnerCenter:partnerLeadList:actions:bookInternalMeeting')!} iconName="AddOnlineMeeting" runOnRec onClick={(req:any)=>{
                                navigate("/app/partner/bookingAvailability?source=Partner&mode=internal&leadId="+req.id+"&leadName="+req.companyName);
                            }}>
                        </ActionEntry>
                    </ActionArea>
                    <ActionArea category={SystemActionCategory.Process}>
                        {!partnerBusinessContract.cspLicenseManagementEnable && (
                            <ActionEntry name="setAsCustomer" label={t('partnerCenter:partnerLeadList:actions:setAsCustomer')!} iconName="TriggerUser" isPromoted runOnRec onClick={(req:any) => {
                                setAsCustomer(req.id);
                            }} />
                        )}
                        <ActionEntry name="sendTrialExpirationEmail" label={t('partnerCenter:partnerLeadList:actions:sendTrialExpirationEmail')!} iconName="Send" runOnRec onClick={(req: any) => {
                            sendTrialExpirationEmail(req.id, partnerStateContext.partner.id);
                        }} />
                    </ActionArea>
                </PageDefinitionContainer>
                <PageDefinitionContainer type={ContainerType.PageStructure}>
                    <ListHeaderEntry name={t('partnerCenter:partnerCustomerList:fields:statusCode')!} fieldName="statusCode" minWidth={150} maxWidth={500} onRender={(item: any, index, column) => {
                        return dsStates.find(ds => ds.code === item.statusCode)?.description;
                    }} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:firstName')!} fieldName="firstname" minWidth={200} maxWidth={250} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:lastName')!} fieldName="lastname" minWidth={200} maxWidth={250} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:companyName')!} fieldName="companyName" minWidth={200} maxWidth={250} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:email')!} fieldName="email" minWidth={300} maxWidth={350} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:phone')!} fieldName="phone" minWidth={150} maxWidth={200} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:partnerLicensesEnabled')!} fieldName="partnerLicensesEnabled" minWidth={150} maxWidth={200} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:createdOn')!} fieldName="createdOn" minWidth={200} maxWidth={250} onRender={(item: any, index, column)=>{
                        if (item.createdOn !== undefined)
                        {
                            let convert: Date = new Date(item.createdOn);
                            let day, month, year, hours, minutes;
                            
                            if (convert.getFullYear() === 1)
                                return undefined;
                                
                            if (convert !== undefined)
                            {
                                day = convert.getDate();
                                month = convert.getMonth() + 1;
                                year = convert.getFullYear();
                                hours = ("0" + convert.getHours()).slice(-2);
                                minutes = ("0" + convert.getMinutes()).slice(-2);
                                return weekday[convert.getDay()] + " " + day + "/" + month + "/" + year + " " + hours + ":" + minutes;
                            }
                        }
                        
                        return undefined;
                    }} />
                    <ListHeaderEntry name={t("partnerCenter:partnerLeadList:fields:trialStartDate")!} fieldName="trialStartDate" minWidth={300} maxWidth={350}
                            onRender={(item: any, index, column) => {
                                if (item.trialStartDate === undefined || item.trialStartDate === null)
                                    return undefined;

                                let date = new Date(item.trialStartDate);
                                if (date.getFullYear() === 1)
                                    return undefined;

                                const milliseconds = Date.UTC(
                                    date.getFullYear(),
                                    date.getMonth(),
                                    date.getDate(),
                                    date.getHours(),
                                    date.getMinutes(),
                                    date.getSeconds(),
                                );
                                const localDate = new Date(milliseconds);
                                return formatDate(localDate);
                            }}
                        />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:internalMeetingDate')!} fieldName="internalMeetingDate" minWidth={200} maxWidth={250} onRender={(item: any, index, column)=>{
                        if (item.internalMeetingDate !== undefined)
                        {
                           let convert: Date = new Date(item.internalMeetingDate);
                           let day, month, year, hours, minutes;

                           if (convert.getFullYear() === 1)
                               return undefined;
                               
                           if (convert !== undefined)
                           {
                               day = convert.getDate();
                               month = convert.getMonth() + 1;
                               year = convert.getFullYear();
                               hours = ("0" + convert.getHours()).slice(-2);
                               minutes = ("0" + convert.getMinutes()).slice(-2);
                               return weekday[convert.getDay()] + " " + day + "/" + month + "/" + year + " " + hours + ":" + minutes;
                           }
                       }
               
                       return undefined;
                    }} />
                    <ListHeaderEntry name={t('partnerCenter:partnerLeadList:fields:demoDate')!} fieldName="demoDate" minWidth={200} maxWidth={250} onRender={(item: any, index, column)=>{
                        if (item.demoDate !== undefined)
                        {
                           let convert: Date = new Date(item.demoDate);
                           let day, month, year, hours, minutes;

                           if (convert.getFullYear() === 1)
                               return undefined;
                               
                           if (convert !== undefined)
                           {
                               day = convert.getDate();
                               month = convert.getMonth() + 1;
                               year = convert.getFullYear();
                               hours = ("0" + convert.getHours()).slice(-2);
                               minutes = ("0" + convert.getMinutes()).slice(-2);
                               return weekday[convert.getDay()] + " " + day + "/" + month + "/" + year + " " + hours + ":" + minutes;
                           }
                       }
               
                       return undefined;
                    }} />
                </PageDefinitionContainer>
            </PageContainer>
            <PartnerLeadCard ref={cardRef} />
            <CommonLeadModal
                showModal={showLeadModal}
                savingData={leadDataSaveInProgress}
                canScroll={false}
                leadRef={selectedLead!}
                leadConfigDetail={leadConfigDetails}
                leadToLiveChecklist={new LeadToLiveChecklist()}
                industryTypes={industryTypes}
                validationErrors={validationErrors}
                onSave={onSaveLeadModal}
                onCancel={() =>{
                    setShowLeadModal(false);
                }}
                checkLeadData={(configuration: SaveLeadConfigChecklist) => {
                    checkLeadModalData(configuration);
                }}
            />
        </>
    );
}